import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useTheme } from '../../providers/ThemeProvider';
import { LanguageSelector } from '../shared/LanguageSelector';
import Styles from './Dashboard.module.css';
import { useNicheLabels } from './useNicheLabels';
import { Button, Image, Label, useAuth } from '@app/engine';
import { Telemetry } from '@shared/frontend';
import { useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';

/** Navigation entry definition. */
interface INavEntry {
  route: string;
  label: string;
  icon: string;
}

/**
 * Builds the ordered navigation entries.
 *
 * @param leadsLabel - The niche-specific noun for captured callers (e.g. "Patients").
 * @param bookingsLabel - The niche-specific noun for appointments (e.g. "Appointments").
 * @returns The ordered navigation entries.
 */
function GetNavEntries(leadsLabel: string, bookingsLabel: string): INavEntry[] {
  return [
    { route: Constants.Route.Home, label: Localized.NavOverview, icon: '📊' },
    { route: Constants.Route.Calls, label: Localized.NavCalls, icon: '📞' },
    { route: Constants.Route.Texts, label: Localized.NavTexts, icon: '💬' },
    { route: Constants.Route.Leads, label: leadsLabel, icon: '🧲' },
    { route: Constants.Route.Bookings, label: bookingsLabel, icon: '📅' },
    { route: Constants.Route.Notifications, label: Localized.NavNotifications, icon: '🔔' },
    { route: Constants.Route.Profile, label: Localized.NavProfile, icon: '🏢' },
    { route: Constants.Route.Knowledge, label: Localized.NavKnowledge, icon: '📚' },
    { route: Constants.Route.Integrations, label: Localized.NavIntegrations, icon: '🧩' },
    { route: Constants.Route.Billing, label: Localized.NavBilling, icon: '🧾' },
    { route: Constants.Route.Invoices, label: Localized.NavInvoices, icon: '📄' },
    { route: Constants.Route.Help, label: Localized.AboutNavHelp, icon: '❓' }
  ];
}

/**
 * The dashboard shell: sidebar navigation, top bar, and content area.
 *
 * @param props - Component props.
 * @param props.title - The page title shown in the top bar.
 * @param props.children - The page content.
 * @returns The layout element.
 */
export function DashboardLayout({ title, children }: { title: string; children: ReactNode }) {
  const { logout } = useAuth();
  const { tenantUser, businessName } = useDashboard();
  const { getThemedAssetDTO } = useTheme();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const labels = useNicheLabels();
  const entries = GetNavEntries(labels.leads, labels.bookings);
  // The Vertex Echo wordmark (icon + product name baked in), so the sidebar brand needs no separate text label.
  const logoAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Preload, 'Logo.png'), [getThemedAssetDTO]);

  return (
    <div className={Styles.root}>
      {navOpen ? <div className={Styles.backdrop} onClick={() => setNavOpen(false)} /> : null}

      <aside className={`${Styles.sidebar} ${navOpen ? Styles.sidebarOpen : ''}`}>
        <Image
          title={Localized.DashName}
          assetDTO={logoAssetDTO}
          classNameContainer={Styles.brand}
          classNameImageEnabled={Styles.brandWordmark}
          onClickHandler={() => {
            setNavOpen(false);
            navigate(Constants.Route.Landing);
          }}
        />

        <nav className={Styles.nav}>
          {entries.map((entry) => (
            <NavLink
              key={entry.route}
              to={entry.route}
              end={entry.route === Constants.Route.Home}
              onClick={() => {
                Telemetry.Navigation(entry.route);
                setNavOpen(false);
              }}
              className={({ isActive }) => `${Styles.navItem} ${isActive ? Styles.navItemActive : ''}`}
            >
              {/* Raw <span>: decorative nav icon flagged aria-hidden; engine Label cannot set aria-hidden and would add a glyph title tooltip. */}
              <span className={Styles.navIcon} aria-hidden="true">
                {entry.icon}
              </span>
              {/* Engine Label: its `title` defaults to `text`, so the full nav label is the native hover tooltip when the visible text is ellipsis-truncated. */}
              <Label classNameContainer={Styles.navLabel} text={entry.label} />
            </NavLink>
          ))}
        </nav>

        <div className={Styles.sidebarFoot}>
          <Button text={Localized.DashSignOut} title={Localized.DashSignOutHint} classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`} onClickHandler={() => logout()} />
        </div>
      </aside>

      <div className={Styles.main}>
        <header className={Styles.topbar}>
          {/* Full-bleed background strip behind the sticky topbar. Lives INSIDE the header (absolute, inset 0) so it always matches the header's height — including the taller two-row portrait
              layout — instead of a hardcoded strip height drifting out of sync. */}
          <div className={Styles.topbarBackdrop} aria-hidden="true" />
          <div className={Styles.topbarLeft}>
            <Button text={'\u2630'} title={Localized.DashNavToggle} classNameButtonEnabled={Styles.mobileNavToggle} onClickHandler={() => setNavOpen((open) => !open)} />
            <Label classNameContainer={Styles.topbarTitle} text={title} />
          </div>
          <div className={Styles.topbarRight}>
            <LanguageSelector />
            {tenantUser ? (
              <>
                {/* Raw <span>: decorative separator glyph flagged aria-hidden; engine Label cannot set aria-hidden and would add a glyph title tooltip. */}
                <span className={Styles.navSeparator} aria-hidden="true">
                  {Constants.Separator}
                </span>
                {/* The BUSINESS name, not the signed-in member's name; falls back to the member while the tenant read is in flight (or failed). */}
                <Label classNameContainer={Styles.bizName} text={businessName ?? tenantUser.displayName} />
              </>
            ) : null}
          </div>
        </header>

        <main className={Styles.content}>{children}</main>
      </div>
    </div>
  );
}
